/**
 * f12.3: the frozen banner on the review, grid and cycle pages (design D8).
 *
 * Frozen: who locked it, when, which snapshot and the rate frozen with it.
 * Reopened (In review with a kept pointer): the snapshot that remains the
 * baseline until the cycle is locked again.
 *
 * Every page passes what its own payload already carries -- the cycle body,
 * the review's `cycle` or the grid's `cycle` all include `current_snapshot` --
 * so no page reads the Finance/Admin-only cycle route just to draw a banner.
 */

import { Lock } from 'lucide-react';

import type { CycleSnapshotRef } from '@/api/client';

import { releaseDate } from '../steps/release/releaseView';

export interface BannerSource {
  stateCode: string;
  snapshot: CycleSnapshotRef | null | undefined;
  reopened: boolean;
}

/** The banner's line, or null when the cycle shows none. */
export function frozenBannerText({ stateCode, snapshot, reopened }: BannerSource): string | null {
  if (!snapshot) return null;
  if (stateCode === 'FROZEN') {
    return (
      `Frozen ${releaseDate(snapshot.taken_at)} by ${snapshot.taken_by.display_name}` +
      ` · snapshot #${snapshot.sequence} · USD rate ${snapshot.usd_rate_to_eur}`
    );
  }
  if (stateCode === 'IN_REVIEW' && reopened) {
    return `Reopened — baseline remains snapshot #${snapshot.sequence} until re-frozen`;
  }
  return null;
}

export function FrozenBanner(source: BannerSource) {
  const text = frozenBannerText(source);
  if (!text) return null;
  return (
    <div className="alert alert-info frozen-banner" role="status" data-testid="frozen-banner">
      <Lock aria-hidden="true" />
      <div>
        <div className="a-title">{text}</div>
      </div>
    </div>
  );
}
